import { PhoneInput } from '@strata/react-inputs';
import { validationStrings } from '@strata/strata-ui-strings';
import * as Yup from 'yup';

const userBaseSchema = (isCreate) => ({
    upn: Yup.string().email().required(validationStrings.requiredField.errorMessage),
    ...(isCreate
        ? {
              given_name: Yup.string()
                  .max(50, validationStrings.maxCharacter[50])
                  .required(validationStrings.requiredField.errorMessage),
              family_name: Yup.string()
                  .max(50, validationStrings.maxCharacter[50])
                  .required(validationStrings.requiredField.errorMessage),
              password: Yup.string()
                  .min(8, 'Password needs at least 8 characters')
                  .required(validationStrings.requiredField.errorMessage)
                  .test('special-chars', 'Password must have !@#$%^&*', (value) =>
                      /[!@#$%^&*]/.test(value),
                  )
                  .test(
                      'mixed-chars',
                      'Password must have upper and lowercase',
                      (value) => /[A-Z]/.test(value) && /[a-z]/.test(value),
                  )
                  .test('number-chars', 'Password must have at least one number (0-9)', (value) =>
                      /\d/.test(value),
                  )
                  .test(
                      'repeat-chars',
                      'Password can have no more than 2 identical characters in a row',
                      (value) => {
                          if (value) {
                              let work = null;
                              for (let i = 0; i < value.length; i += 1) {
                                  const c = value[i];
                                  if (work && work[0] === c) {
                                      work += c;
                                      if (work.length > 2) {
                                          return false;
                                      }
                                  } else {
                                      work = c;
                                  }
                              }
                              return true;
                          }
                          return false;
                      },
                  ),
          }
        : {
              full_name: Yup.string()
                  .max(100, validationStrings.maxCharacter[100])
                  .required(validationStrings.requiredField.errorMessage),
          }),
    sms_number: PhoneInput.validator().nullable(),
});

export const getPrescriberUserSchema = (isCreate) =>
    Yup.object({
        ...userBaseSchema(isCreate),
        practice_npi: Yup.string()
            .length(10, 'Must be exactly 10 digits')
            .required(validationStrings.requiredField.errorMessage),
        identities: Yup.array()
            .of(
                Yup.object().shape({
                    prescriber_npi: Yup.string()
                        .length(10, 'Must be exactly 10 digits')
                        .required(validationStrings.requiredField.errorMessage),
                    role_name: Yup.string().required(validationStrings.requiredField.errorMessage),
                }),
            )
            .required(validationStrings.requiredField.errorMessage)
            .min(1, 'Minimum of 1 identity'),
    });

export const getSupplierUserSchema = (isCreate) =>
    Yup.object({
        ...userBaseSchema(isCreate),
        supplier: Yup.object().required(validationStrings.requiredField.errorMessage),
        role: Yup.object().required(validationStrings.requiredField.errorMessage),
        analytics_access: Yup.bool().required(validationStrings.requiredField.errorMessage),
    });

export const getPayerUserSchema = (isCreate) =>
    Yup.object({
        ...userBaseSchema(isCreate),
        payers: Yup.array()
            .required(validationStrings.requiredField.errorMessage)
            .min(1, validationStrings.requiredField.errorMessage),
        payer_segments: Yup.array()
            .required(validationStrings.requiredField.errorMessage)
            .min(1, validationStrings.requiredField.errorMessage),
        role: Yup.object().required(validationStrings.requiredField.errorMessage),
        restricted_access: Yup.bool().required(validationStrings.requiredField.errorMessage),
        analytics_access: Yup.bool().required(validationStrings.requiredField.errorMessage),
        pbm_id: Yup.object(),
    });

export const getCompassUserSchema = (isCreate) =>
    Yup.object({
        ...userBaseSchema(isCreate),
        role: Yup.object().required(validationStrings.requiredField.errorMessage),
        payer_segments: Yup.array()
            .required(validationStrings.requiredField.errorMessage)
            .min(1, validationStrings.requiredField.errorMessage),
    });

export const getLighthouseUserSchema = (isCreate) =>
    Yup.object({
        ...userBaseSchema(isCreate),
        role: Yup.object().required(validationStrings.requiredField.errorMessage),
    });
